import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PreviewIcon from '@mui/icons-material/Preview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ReportPreview from './ReportPreview';
import ReportHistory from './ReportHistory';
import { useNotification } from '../contexts/NotificationContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  FREQUENCY_OPTIONS,
  DAYS_OF_WEEK,
  createScheduleSlot,
  normalizeReportSchedule,
  getScheduleDescription,
  buildSchedulePayload,
  parseEmailList,
  isValidEmail,
  getNextSlotRunHint,
} from '../utils/reportSchedule';
import {
  REPORT_TIMEZONE_IDS,
  formatTimezoneLabel,
  resolveTimezoneSelection,
  defaultBrowserTimezone,
} from '../utils/timezones';

const ReportManager = ({ open, onClose, savedDashboards, isAuthenticated, authToken, managerId, adminId, currentDashboard, onSaveCurrentDashboard, suggestedTimezone }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reportName, setReportName] = useState('');
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleSlots, setScheduleSlots] = useState([createScheduleSlot()]);
  const [emailRecipientsText, setEmailRecipientsText] = useState('');
  const [pdfMode, setPdfMode] = useState('combined');
  const [timezone, setTimezone] = useState(defaultBrowserTimezone());

  const initialTimezone = () =>
    suggestedTimezone && REPORT_TIMEZONE_IDS.includes(suggestedTimezone)
      ? suggestedTimezone
      : defaultBrowserTimezone();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [scheduleNow, setScheduleNow] = useState(() => new Date());

  // Helper para obtener headers de autenticación
  const getAuthHeaders = () => {
    const headers = {
      'Authorization': authToken
    };
    if (adminId) {
      headers['X-Admin-Id'] = adminId;
    } else if (managerId) {
      headers['X-Manager-Id'] = managerId;
    }
    return headers;
  };

  useEffect(() => {
    if (!editDialogOpen || !scheduleEnabled) return undefined;
    setScheduleNow(new Date());
    const id = setInterval(() => setScheduleNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [editDialogOpen, scheduleEnabled, scheduleSlots, timezone]);

  const reportsQueryKey = ['reports', managerId, adminId];
  const canLoadReports = Boolean(isAuthenticated && authToken && (managerId || adminId));
  const reportsQuery = useQuery({
    queryKey: reportsQueryKey,
    queryFn: async () => {
      const response = await axios.get('/api/reports/list', { headers: getAuthHeaders() });
      if (!response.data.success) throw new Error('No se pudieron cargar los reportes');
      return {
        reports: (response.data.reports || []).filter(Boolean),
        dashboards: (response.data.dashboards || []).filter(Boolean),
      };
    },
    enabled: open && canLoadReports,
  });
  const reports = React.useMemo(() => reportsQuery.data?.reports || [], [reportsQuery.data]);
  const reportListDashboards = React.useMemo(() => reportsQuery.data?.dashboards || [], [reportsQuery.data]);
  const loadReports = () => reportsQuery.refetch();

  // Lista unificada para resolver dashboards en preview: servidor + prop (incluye recién guardados)
  const dashboardsForPreview = React.useMemo(() => {
    const byId = new Map();
    (reportListDashboards || []).forEach(d => d && d.id != null && byId.set(Number(d.id), d));
    (savedDashboards || []).forEach(d => d && d.id != null && byId.set(Number(d.id), d));
    return Array.from(byId.values());
  }, [reportListDashboards, savedDashboards]);

  const handleCreateNew = async () => {
    // Si hay un dashboard actual no guardado, guardarlo automáticamente
    if (currentDashboard && onSaveCurrentDashboard && currentDashboard.queryData) {
      // Verificar si el dashboard actual ya está guardado comparando por contenido
      const isAlreadySaved = savedDashboards.some(d => 
        d && 
        d.query === currentDashboard.query &&
        d.database === currentDashboard.database &&
        JSON.stringify(d.charts) === JSON.stringify(currentDashboard.charts)
      );
      
      if (!isAlreadySaved) {
        try {
          await onSaveCurrentDashboard();
          // savedDashboards se actualizará automáticamente desde App.js
        } catch (error) {
          console.error('Error al guardar dashboard automáticamente:', error);
          // Continuar de todas formas
        }
      }
    }
    
    setEditingReport(null);
    setReportName('');
    setSelectedDashboards([]);
    setScheduleEnabled(false);
    setScheduleSlots([createScheduleSlot()]);
    setEmailRecipientsText('');
    setPdfMode('combined');
    setTimezone(initialTimezone());
    setEditDialogOpen(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setReportName(report.name || '');
    // report.dashboards puede ser [id1, id2] desde el API; resolver a objetos para no guardar d.id = undefined
    const list = dashboardsForPreview || savedDashboards || [];
    const resolved = (report.dashboards || []).map((dashRef) => {
      if (dashRef && typeof dashRef === 'object' && dashRef.id != null) return dashRef;
      const id = dashRef != null ? (typeof dashRef === 'string' ? parseInt(dashRef, 10) : dashRef) : null;
      if (id == null || Number.isNaN(id)) return null;
      const found = list.find((d) => d && (Number(d.id) === Number(id) || String(d.id) === String(id)));
      return found || { id, name: `Dashboard ${id}` };
    }).filter(Boolean);
    setSelectedDashboards(resolved);
    const norm = normalizeReportSchedule(report.schedule);
    setScheduleEnabled(norm.enabled);
    setScheduleSlots(norm.slots.length ? norm.slots : [createScheduleSlot()]);
    setEmailRecipientsText(norm.emailRecipients.join(', '));
    setPdfMode(norm.pdfMode || 'combined');
    setTimezone(norm.timezone || initialTimezone());
    setEditDialogOpen(true);
  };

  const handlePreview = async (report) => {
    // Refrescar reportes y dashboards para ver la versión actualizada de cada dashboard
    await loadReports();
    setPreviewReport(report);
    setPreviewOpen(true);
  };

  const deleteReportMutation = useMutation({
    mutationFn: (reportId) => axios.delete(`/api/reports/${reportId}`, { headers: getAuthHeaders() }),
    onSuccess: (_data, reportId) => {
      queryClient.setQueryData(reportsQueryKey, (old) =>
        old ? { ...old, reports: (old.reports || []).filter((r) => r && r.id !== reportId) } : old
      );
      showSuccess('Reporte eliminado exitosamente');
    },
    onError: (error) => {
      console.error('Error al eliminar reporte:', error);
      showError('Error al eliminar reporte. Por favor, intenta de nuevo.');
    },
  });

  const handleDelete = (reportId) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para eliminar reportes');
      return;
    }
    if (window.confirm('¿Eliminar este reporte?')) {
      deleteReportMutation.mutate(reportId);
    }
  };

  const saveReportMutation = useMutation({
    mutationFn: async (report) => {
      const response = await axios.post('/api/reports/save', report, { headers: getAuthHeaders() });
      if (!response.data.success) throw new Error('No se pudo guardar el reporte');
      return { ...report, id: response.data.id };
    },
  });

  const handleSaveReport = async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para guardar reportes');
      return;
    }
    
    if (!reportName.trim()) {
      showWarning('Por favor, ingresa un nombre para el reporte');
      return;
    }

    if (selectedDashboards.length === 0) {
      showWarning('Por favor, selecciona al menos un dashboard');
      return;
    }

    // Si hay un dashboard actual no guardado y está seleccionado, guardarlo primero
    if (currentDashboard && onSaveCurrentDashboard && currentDashboard.queryData) {
      // Verificar si el dashboard actual ya está guardado comparando por contenido
      const isAlreadySaved = savedDashboards.some(d => 
        d && 
        d.query === currentDashboard.query &&
        d.database === currentDashboard.database &&
        JSON.stringify(d.charts) === JSON.stringify(currentDashboard.charts)
      );
      
      // Verificar si el dashboard actual está en los seleccionados (comparando por contenido)
      const isCurrentDashboardSelected = selectedDashboards.some(sd => 
        sd && 
        sd.query === currentDashboard.query &&
        sd.database === currentDashboard.database &&
        JSON.stringify(sd.charts) === JSON.stringify(currentDashboard.charts)
      );
      
      if (isCurrentDashboardSelected && !isAlreadySaved) {
        try {
          const savedDashboard = await onSaveCurrentDashboard();
          // Actualizar selectedDashboards con el dashboard guardado
          if (savedDashboard) {
            setSelectedDashboards(selectedDashboards.map(sd => {
              // Reemplazar el dashboard temporal con el guardado si coincide por contenido
              if (sd && 
                  sd.query === currentDashboard.query &&
                  sd.database === currentDashboard.database &&
                  JSON.stringify(sd.charts) === JSON.stringify(currentDashboard.charts)) {
                return savedDashboard;
              }
              return sd;
            }));
          }
        } catch (error) {
          console.error('Error al guardar dashboard automáticamente:', error);
          showError('Error al guardar el dashboard actual. Por favor, guárdalo manualmente antes de crear el reporte.');
          return;
        }
      }
    }

    if (scheduleEnabled && scheduleSlots.length === 0) {
      showWarning('Agrega al menos un horario de generación automática');
      return;
    }

    const invalidEmails = String(emailRecipientsText || '')
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter((s) => s && !isValidEmail(s));
    if (invalidEmails.length) {
      showWarning(`Correos inválidos: ${invalidEmails.join(', ')}`);
      return;
    }

    const schedule = buildSchedulePayload({
      enabled: scheduleEnabled,
      timezone: resolvedScheduleTimezone,
      emailRecipientsText,
      slots: scheduleSlots,
      pdfMode,
    });

    const report = {
      id: editingReport?.id ?? null,
      name: reportName.trim(),
      dashboards: (selectedDashboards || []).map((d) => d && d.id).filter((id) => id != null && id !== ''),
      schedule,
      createdAt: editingReport?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    try {
      const saved = await saveReportMutation.mutateAsync(report);
      queryClient.setQueryData(reportsQueryKey, (old) => {
        const prev = (old?.reports || []).filter(Boolean);
        const updatedReports = editingReport
          ? prev.map((r) => (r && r.id === editingReport.id ? saved : r)).filter(Boolean)
          : [...prev, saved];
        return { ...old, reports: updatedReports };
      });
      setEditDialogOpen(false);
      loadReports(); // refrescar lista y dashboards para que la previsualización resuelva bien
      showSuccess(`Reporte "${reportName}" ${editingReport ? 'actualizado' : 'creado'} exitosamente`);
    } catch (error) {
      console.error('Error al guardar reporte:', error);
      const msg = error.response?.data?.error || error.message || 'Error al guardar reporte. Por favor, intenta de nuevo.';
      showError(msg);
    }
  };

  const handleAddDashboard = (dashboardId) => {
    const dashboard = savedDashboards.find(d => d && d.id === dashboardId);
    if (dashboard && !selectedDashboards.find(d => d && d.id === dashboardId)) {
      setSelectedDashboards([...selectedDashboards, dashboard]);
    }
  };

  const handleRemoveDashboard = (dashboardId) => {
    setSelectedDashboards(selectedDashboards.filter(d => d.id !== dashboardId));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const items = [...selectedDashboards];
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    setSelectedDashboards(items);
  };

  const handleMoveDown = (index) => {
    if (index === selectedDashboards.length - 1) return;
    const items = [...selectedDashboards];
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    setSelectedDashboards(items);
  };

  const updateScheduleSlot = (index, patch) => {
    setScheduleSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot))
    );
  };

  const handleAddScheduleSlot = () => {
    setScheduleSlots((prev) => [...prev, createScheduleSlot({ time: '18:00' })]);
  };

  const handleRemoveScheduleSlot = (index) => {
    setScheduleSlots((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const runNowMutation = useMutation({
    mutationFn: (report) =>
      axios.post(`/api/reports/${report.id}/run-now`, {}, { headers: getAuthHeaders(), timeout: 30000 }),
  });
  const runningReportId = runNowMutation.isPending ? runNowMutation.variables?.id : null;

  const handleRunNow = async (report) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para ejecutar reportes');
      return;
    }
    if (!report?.id) return;

    try {
      const response = await runNowMutation.mutateAsync(report);
      if (response.data.success && response.data.accepted) {
        showSuccess(
          response.data.message ||
            `Reporte "${report.name}" en ejecución. Revisa Ver Historial en 1–2 minutos.`
        );
        return;
      }
      if (response.data.success) {
        const emailNote = response.data.result?.emailError
          ? ` (PDF ok, correo: ${response.data.result.emailError})`
          : response.data.result?.emailSent
            ? ' (PDF y correo enviados)'
            : '';
        showSuccess(`Reporte "${report.name}" ejecutado correctamente${emailNote}`);
      }
    } catch (error) {
      const detail =
        error.response?.data?.error ||
        error.message ||
        'Error al ejecutar el reporte';
      console.error('Error run-now:', error.response?.data || error);
      showError(`Error al ejecutar el reporte: ${detail}`);
    }
  };

  const getScheduleDescriptionForReport = (report) => getScheduleDescription(report);

  const resolvedScheduleTimezone = resolveTimezoneSelection(
    timezone,
    suggestedTimezone || defaultBrowserTimezone(),
    REPORT_TIMEZONE_IDS
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Gestión de Reportes</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryOpen(true)}
            >
              Ver Historial
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Nuevo Reporte
            </Button>
          </Box>

          {reports.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No hay reportes creados. Crea uno nuevo para comenzar.
            </Typography>
          ) : (
            <List>
              {(reports || []).filter(Boolean).map((report, index) => (
                <Paper key={report?.id ?? `report-${index}`} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{report?.name ?? 'Sin nombre'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {(report?.dashboards?.length ?? 0)} dashboard{(report?.dashboards?.length ?? 0) !== 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {getScheduleDescriptionForReport(report)}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRunNow(report)}
                        sx={{ mr: 1 }}
                        title="Ejecutar ahora"
                        disabled={runningReportId === report?.id}
                      >
                        {runningReportId === report?.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <PlayArrowIcon />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(report)}
                        sx={{ mr: 1 }}
                        title="Previsualizar"
                      >
                        <PreviewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(report)}
                        sx={{ mr: 1 }}
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(report?.id)}
                        color="error"
                        title="Eliminar"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de creación/edición */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingReport ? 'Editar Reporte' : 'Nuevo Reporte'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Nombre del Reporte"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              fullWidth
              required
            />

            <Divider>Dashboards</Divider>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Seleccionar Dashboard
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Agregar Dashboard</InputLabel>
                <Select
                  value=""
                  label="Agregar Dashboard"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddDashboard(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  {savedDashboards
                    .filter(d => d && d.id) // Filtrar valores null/undefined
                    .filter(d => !selectedDashboards.find(sd => sd && sd.id === d.id))
                    .map((dashboard) => (
                      <MenuItem key={dashboard.id} value={dashboard.id}>
                        {dashboard.name || 'Dashboard sin nombre'}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {selectedDashboards.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Orden de Dashboards (usa las flechas para reordenar)
                  </Typography>
                  <List>
                    {selectedDashboards.map((dashboard, index) => (
                      <Paper
                        key={dashboard?.id ?? `dashboard-${index}`}
                        sx={{
                          mb: 1,
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            title="Mover arriba"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === selectedDashboards.length - 1}
                            title="Mover abajo"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <ListItemText
                          primary={dashboard.name || 'Dashboard sin nombre'}
                          secondary={`${index + 1} de ${selectedDashboards.length}`}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveDashboard(dashboard.id)}
                          color="error"
                          title="Eliminar"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Paper>
                    ))}
                  </List>
                </Box>
              )}
            </Box>

            <Divider>Programación</Divider>

            <FormControlLabel
              control={
                <Switch
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                />
              }
              label="Habilitar generación automática de PDF"
            />

            {scheduleEnabled && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Zona Horaria</InputLabel>
                      <Select
                        value={resolvedScheduleTimezone}
                        label="Zona Horaria"
                        onChange={(e) => setTimezone(e.target.value)}
                      >
                        {REPORT_TIMEZONE_IDS.map((tz) => (
                          <MenuItem key={tz} value={tz}>
                            {formatTimezoneLabel(tz)}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Usa el identificador IANA (ej. America/Santiago). El offset GMT se ajusta solo en invierno y verano.
                        {suggestedTimezone && suggestedTimezone !== resolvedScheduleTimezone && (
                          <> El dashboard usa {formatTimezoneLabel(suggestedTimezone)}.</>
                        )}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="subtitle2">Horarios de generación</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddScheduleSlot}>
                    Agregar horario
                  </Button>
                </Box>

                {scheduleSlots.map((slot, index) => (
                  <Paper key={slot.id || index} variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Frecuencia</InputLabel>
                          <Select
                            value={slot.frequency}
                            label="Frecuencia"
                            onChange={(e) => updateScheduleSlot(index, { frequency: e.target.value })}
                          >
                            {FREQUENCY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {slot.frequency === 'weekly' && (
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Día</InputLabel>
                            <Select
                              value={slot.dayOfWeek}
                              label="Día"
                              onChange={(e) => updateScheduleSlot(index, { dayOfWeek: e.target.value })}
                            >
                              {DAYS_OF_WEEK.map((day) => (
                                <MenuItem key={day.value} value={day.value}>
                                  {day.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {slot.frequency === 'monthly' && (
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Día del mes"
                            type="number"
                            value={slot.dayOfMonth}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (val >= 1 && val <= 28) {
                                updateScheduleSlot(index, { dayOfMonth: val });
                              }
                            }}
                            inputProps={{ min: 1, max: 28 }}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            label="Hora"
                            type="time"
                            value={slot.time}
                            onChange={(e) => updateScheduleSlot(index, { time: e.target.value })}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                          <Tooltip
                            title={
                              <span style={{ whiteSpace: 'pre-line' }}>
                                {getNextSlotRunHint(slot, resolvedScheduleTimezone, scheduleNow)}
                              </span>
                            }
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" sx={{ flexShrink: 0 }} aria-label="Próxima ejecución">
                              <InfoOutlinedIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveScheduleSlot(index)}
                          disabled={scheduleSlots.length <= 1}
                          title="Quitar horario"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </>
            )}

            <Divider sx={{ mt: 2 }}>Correo</Divider>

            <FormControlLabel
              control={
                <Switch
                  checked={pdfMode === 'perDashboard'}
                  onChange={(e) => setPdfMode(e.target.checked ? 'perDashboard' : 'combined')}
                  disabled={selectedDashboards.length < 2}
                />
              }
              label="Un PDF por dashboard (en vez de un PDF combinado)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
              {selectedDashboards.length < 2
                ? 'Agrega al menos 2 dashboards para habilitar esta opción.'
                : 'Aplica al correo (adjuntos) y a la descarga del historial. El PDF combinado junta todos los dashboards en un archivo.'}
            </Typography>

            <TextField
              label="Destinatarios (correo)"
              value={emailRecipientsText}
              onChange={(e) => setEmailRecipientsText(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="uno@empresa.com, otro@empresa.com"
              helperText="Se usan al ejecutar ahora (play) y en la generación automática. Configura AWS SES en Admin → Reportes."
            />
            {parseEmailList(emailRecipientsText).length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {parseEmailList(emailRecipientsText).length} destinatario(s) válido(s)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={() => {
              const tempReport = {
                id: editingReport?.id || Date.now(),
                name: reportName.trim(),
                dashboards: selectedDashboards,
                schedule: buildSchedulePayload({
                  enabled: scheduleEnabled,
                  timezone: resolvedScheduleTimezone,
                  emailRecipientsText,
                  slots: scheduleSlots,
                  pdfMode,
                }),
              };
              handlePreview(tempReport);
            }}
            variant="outlined"
          >
            Previsualizar
          </Button>
          <Button onClick={handleSaveReport} variant="contained">
            {editingReport ? 'Actualizar' : 'Crear'} Reporte
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <ReportPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        report={previewReport}
        savedDashboards={dashboardsForPreview}
        getAuthHeaders={getAuthHeaders}
        currentDashboard={currentDashboard}
      />

      {/* History Dialog */}
      <ReportHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        savedDashboards={savedDashboards}
        isAuthenticated={isAuthenticated}
        authToken={authToken}
        managerId={managerId}
        adminId={adminId}
      />
    </>
  );
};

export default ReportManager;
